(() => {
  const nativeGrid = document.querySelector('.main-products-grid');
  const host = document.getElementById('cm_results')
    || nativeGrid?.closest('.shopify-section') || nativeGrid?.parentElement;
  const config = document.getElementById('sac-culture-catalog-entries-config');
  if (!host || !config || !window.customElements || !window.IntersectionObserver) return;

  const cardSelector = 'product-card.card--product';
  const observed = new WeakSet();
  const visible = new WeakSet();
  const processing = new WeakSet();
  const pending = new Set();
  const products = new Map();
  const requests = [];
  let activeRequests = 0;
  let scheduled;

  const root = new URL(config.dataset.root || '/', window.location.origin);
  if (root.origin !== window.location.origin) return;
  root.pathname = root.pathname.replace(/\/?$/, '/');

  function productId(value) {
    return /^(?:gid:\/\/shopify\/Product\/)?(\d+)$/.exec(String(value || ''))?.[1] || '';
  }

  function referenceFor(card) {
    const link = card.querySelector('.card__title a[href], a.js-prod-link[href], a[href*="/products/"]');
    if (!link) return null;
    let url;
    let handle;
    try {
      url = new URL(link.getAttribute('href'), window.location.href);
      handle = decodeURIComponent(/\/products\/([^/]+)\/?$/.exec(url.pathname)?.[1] || '');
    } catch {
      return null;
    }
    if (url.origin !== window.location.origin || !/^[\p{L}\p{N}][\p{L}\p{N}_-]*$/u.test(handle)) return null;
    const form = card.querySelector('form[id^="quick-add-"]');
    const input = card.querySelector('input[name="product-id"]');
    const id = productId(card.dataset.productId)
      || productId(form?.getAttribute('id')?.replace(/^quick-add-/, ''))
      || productId(input?.value);
    return {handle, id};
  }

  function pump() {
    while (activeRequests < 4 && requests.length) {
      const {handle, resolve} = requests.shift();
      const url = new URL('products/' + encodeURIComponent(handle) + '.js', root);
      activeRequests++;
      fetch(url.href, {
        headers: {Accept: 'application/json'},
        credentials: 'same-origin',
        signal: AbortSignal.timeout(8000)
      })
        .then(response => response.ok ? response.json() : null)
        .then(product => {
          if (!product || product.handle !== handle || !Number.isSafeInteger(product.id)
            || !Number.isSafeInteger(product.price_min) || !Number.isSafeInteger(product.price_max)
            || product.price_min < 0 || product.price_max < product.price_min) return resolve(null);
          resolve(product);
        })
        .catch(() => resolve(null))
        .finally(() => {
          activeRequests--;
          pump();
        });
    }
  }

  function getProduct(handle) {
    if (!products.has(handle)) {
      const request = new Promise(resolve => requests.push({handle, resolve})).then(product => {
        if (!product) products.delete(handle);
        return product;
      });
      products.set(handle, request);
      pump();
    }
    return products.get(handle);
  }

  async function populate(card) {
    if (!host.contains(card) || !visible.has(card) || processing.has(card)) return;
    const reference = referenceFor(card);
    if (!reference) return;
    const existing = card.querySelector('sac-entry-badge');
    if (existing && (!existing.hasAttribute('data-sac-catalog-entry')
      || existing.dataset.catalogHandle === reference.handle
      && (!reference.id || existing.dataset.productId === reference.id))) return;
    existing?.remove();

    processing.add(card);
    try {
      const product = await getProduct(reference.handle);
      const current = referenceFor(card);
      if (!host.contains(card) || !current) return;
      if (current.handle !== reference.handle || current.id !== reference.id) return;
      if (!product || reference.id && reference.id !== String(product.id)) return;
      if (card.querySelector('sac-entry-badge')) return;
      const price = card.querySelector('.card__info-inner .price');
      if (!price) return;

      const badge = document.createElement('sac-entry-badge');
      badge.className = 'sac-entry-badge';
      badge.hidden = true;
      badge.setAttribute('data-sac-catalog-entry', '');
      Object.assign(badge.dataset, {
        productId: String(product.id),
        catalogHandle: reference.handle,
        shopId: config.dataset.shopId,
        sweepstakeId: config.dataset.sweepstakeId,
        priceMin: String(product.price_min),
        priceMax: String(product.price_max),
        label: config.dataset.label,
        description: config.dataset.description
      });
      const count = document.createElement('span');
      count.className = 'sac-entry-badge__count';
      badge.append(count);
      price.insertAdjacentElement('afterend', badge);
    } finally {
      processing.delete(card);
      const current = referenceFor(card);
      if (host.contains(card) && current
        && (current.handle !== reference.handle || current.id !== reference.id)) enqueue(card);
    }
  }

  function enqueue(card) {
    pending.add(card);
    if (scheduled) return;
    scheduled = setTimeout(() => {
      scheduled = undefined;
      const cards = [...pending];
      pending.clear();
      cards.forEach(card => { void populate(card); });
    }, 100);
  }

  const viewport = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        visible.add(entry.target);
        enqueue(entry.target);
      } else {
        visible.delete(entry.target);
      }
    });
  }, {rootMargin: '300px 0px'});

  function watch(card) {
    if (!host.contains(card) || !card.closest('.cmRepeater_items, .main-products-grid')) return;
    if (!observed.has(card)) {
      observed.add(card);
      viewport.observe(card);
    } else if (visible.has(card)) {
      enqueue(card);
    }
  }

  function collect(node) {
    if (!(node instanceof Element)) return;
    const card = node.closest(cardSelector);
    if (card) watch(card);
    node.querySelectorAll(cardSelector).forEach(watch);
  }

  customElements.whenDefined('sac-entry-badge').then(() => {
    collect(host);
    new MutationObserver(records => {
      records.forEach(record => {
        if (record.target instanceof Element && record.target.closest('sac-entry-badge')) return;
        if (record.target instanceof Element) {
          const card = record.target.closest(cardSelector);
          if (card) watch(card);
        }
        record.addedNodes.forEach(collect);
        record.removedNodes.forEach(node => {
          if (!(node instanceof Element)) return;
          const cards = node.matches(cardSelector) ? [node] : [...node.querySelectorAll(cardSelector)];
          cards.forEach(card => {
            if (host.contains(card)) return;
            viewport.unobserve(card);
            observed.delete(card);
            visible.delete(card);
            pending.delete(card);
          });
        });
      });
    }).observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href', 'value', 'id', 'data-product-id']
    });
  });
})();
